import { useEffect, useRef, useState } from 'react'

interface ArOverlayProps {
  /** Builds the GLB blob for the current book + cover. */
  build: () => Promise<Blob>
  onClose: () => void
}

/**
 * Full-screen overlay that shows the book in <model-viewer>. On AR-capable phones it
 * renders model-viewer's built-in "View in your space" button (which launches iOS Quick
 * Look / Android Scene Viewer within the user's tap). On desktop it's just a 3D preview,
 * which doubles as a way to verify the exported GLB.
 */
export function ArOverlay({ build, onClose }: ArOverlayProps) {
  const stageRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'building' | 'ready' | 'error'>('building')
  const [errorDetail, setErrorDetail] = useState('')

  useEffect(() => {
    let cancelled = false
    let url: string | null = null
    let mv: HTMLElement | null = null
    ;(async () => {
      let stage = 'load 3D viewer'
      try {
        await import('@google/model-viewer') // registers the <model-viewer> element
        stage = 'build 3D model'
        const blob = await build()
        if (cancelled) return
        url = URL.createObjectURL(blob)
        mv = document.createElement('model-viewer')
        mv.setAttribute('camera-controls', '')
        mv.setAttribute('touch-action', 'pan-y')
        mv.setAttribute('ar', '')
        mv.setAttribute('ar-modes', 'webxr scene-viewer quick-look')
        mv.setAttribute('ar-scale', 'fixed') // keep the real 8.5"×11" size in AR
        mv.setAttribute('shadow-intensity', '1')
        mv.setAttribute('exposure', '1')
        mv.setAttribute('environment-image', 'neutral')
        mv.setAttribute('alt', '3D book cover')
        mv.style.width = '100%'
        mv.style.height = '100%'
        mv.style.backgroundColor = 'transparent'
        ;(mv as HTMLElement & { src: string }).src = url
        stageRef.current?.appendChild(mv)
        setStatus('ready')
      } catch (e) {
        if (!cancelled) {
          console.error('AR export failed:', e)
          const msg = e instanceof Error ? `${e.name}: ${e.message}` : String(e)
          setErrorDetail(`Failed to ${stage} — ${msg}`)
          setStatus('error')
        }
      }
    })()
    return () => {
      cancelled = true
      mv?.remove()
      if (url) URL.revokeObjectURL(url)
    }
  }, [build])

  return (
    <div className="ar-overlay">
      <div className="ar-overlay__bar">
        <span className="ar-overlay__title">View in AR / 3D</span>
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="ar-overlay__stage" ref={stageRef} />
      {status === 'building' && (
        <div className="ar-overlay__msg">
          <div className="loader__spinner" />
          <div>Preparing 3D model…</div>
        </div>
      )}
      {status === 'error' && (
        <div className="ar-overlay__msg">
          <div>Couldn’t prepare the AR model.</div>
          {errorDetail && <div className="ar-overlay__err">{errorDetail}</div>}
        </div>
      )}
      {status === 'ready' && (
        <div className="ar-overlay__hint">On a phone, tap the AR button to place it in your space.</div>
      )}
    </div>
  )
}
